import DashboardContainer from "components/dashboard/DashboardContainer";
import { DashboardLayout } from "components/DashboardLayout";
import { TableUI } from "components/design/TableUI";
import { Header } from "components/DesignSystem/Header/Header";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { AuditLogFilter } from "./AuditLogFilter";
import { AuditLogSlider } from "./AuditLogSlider";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { useQuery } from "hooks/useQuery";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { TransitionLog } from "components/AuditlogList/AuditlogList";
import { useMemo } from "react";
import {
  Route,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  AuditLog,
  useGetAuditLogsQuery,
  useGetCompanyGroupAuditLogsQuery,
} from "store/apis/auditlogs";
import {
  AUDIT_LOG_FEATURE,
  AUDIT_LOG_FEATURE_OPTIONS,
  AUDIT_LOG_USER_TYPE,
} from "constants/auditLogFilters";
import { formatDate, formatTime } from "utils/formatDate";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import Wind from "components/icons/Wind";
import { Button } from "components/DesignSystem/Button/Button";

const createAuditLogColumns = createColumnHelper<AuditLog>();

const defaultData: never[] = [];

const featureLabelMap: Record<string, string> = {
  ...Object.fromEntries(
    AUDIT_LOG_FEATURE_OPTIONS.map((opt) => [opt.value, opt.label])
  ),
  [AUDIT_LOG_FEATURE.TRANSACTION_CHANGE]: "Transaction",
  [AUDIT_LOG_FEATURE.TRANSACTION_PULL]: "Transaction",
  [AUDIT_LOG_FEATURE.TRANSACTION_CATEGORY_CHANGE]: "Transaction",
  [AUDIT_LOG_FEATURE.TRANSACTION_VENDOR_CHANGE]: "Transaction",
};

const auditLogColumns = [
  createAuditLogColumns.accessor("company_entity.name", {
    header: "Entity",
    size: 15,
    cell: (info) => {
      return (
        <div className="t-text-body-sm t-text-text-60">
          {info.getValue() || "-"}
        </div>
      );
    },
  }),
  createAuditLogColumns.accessor("event_type", {
    header: "Feature",
    size: 15,
    cell: (info) => {
      const eventType = info.getValue();
      return (
        <div className="t-text-body-sm t-text-text-60">
          {featureLabelMap[eventType || ""] || eventType || "-"}
        </div>
      );
    },
  }),
  createAuditLogColumns.display({
    header: "Latest event",
    size: 35,
    cell: (info) => {
      const log = info.row.original;
      return (
        <div className="t-flex t-flex-col t-w-full">
          <div className="t-text-body-sm t-text-text-60 t-mb-0" role="listitem">
            <TransitionLog log={log} />
          </div>
          <span className="t-text-body-sm-lite t-text-text-30">
            {log.obj?.description}
          </span>
        </div>
      );
    },
  }),
  createAuditLogColumns.display({
    header: "Last updated by",
    size: 15,
    cell: (info) => {
      const { owner } = info.row.original;
      if (owner?.user_type === AUDIT_LOG_USER_TYPE.USER) {
        return (
          <div className="t-text-body-sm t-text-text-60">
            {owner.name || "-"}
          </div>
        );
      }
      return (
        <div className="t-text-body-sm t-text-text-60">
          {owner?.user_type || "-"}
        </div>
      );
    },
  }),
  createAuditLogColumns.accessor("created_at", {
    header: "Last updated on",
    size: 15,
    cell: (info) => {
      const value = info.getValue();
      if (!value) return "-";
      return (
        <div className="t-flex t-flex-col t-w-full">
          <span className="t-text-body-sm t-text-text-60">
            {formatDate(value)}
          </span>
          <span className="t-text-body-sm-lite t-text-text-30">
            {formatTime(value)}
          </span>
        </div>
      );
    },
  }),
];

const AuditLogsTable = ({
  groupId,
  entityId,
  isSingleGroupView,
}: {
  groupId: string;
  entityId: string;
  isSingleGroupView: boolean;
}) => {
  const query = useQuery();
  const page = query.get("page") || "1";
  const pageSize = Number(query.get("page_size") || 20);
  const history = useHistory();
  const { url } = useRouteMatch();
  const { search } = useLocation();

  const permission = useHasSubscriptionPermission({
    route: "/settings/auditlog",
    product_type: "TAX",
    parentEntityId: entityId,
  });

  const blocked = permission?.blocked;

  const resetFilter = () => {
    updateMultipleFilters(initialFilterValue);
  };

  const initialFilterValue = {
    START_DATE: "" as string,
    END_DATE: "" as string,
    SELECT_PERIOD: "" as string,
    FEATURE: [] as string[],
    SOURCE: "" as string,

    GROUPS: [] as string[],
    ENDITY_ID: "" as string,
  };

  const {
    values: filterValues,
    updateFilter,
    updateMultipleFilters,
  } = useFilters({
    initialValue: initialFilterValue,
    useQueryParams: true,
  });

  const featureParam = filterValues.FEATURE?.join(",") || undefined;
  const sourceParam = filterValues.SOURCE || undefined;

  const {
    data,
    isLoading: isAdminLoading,
    isFetching: isAdminFetching,
    isSuccess: isAdminSuccess,
  } = useGetAuditLogsQuery(
    {
      pageNum: Number(page),
      groupIds: filterValues.GROUPS?.join(","),
      startDate: filterValues.START_DATE,
      endDate: filterValues.END_DATE,
      entityId: filterValues.ENDITY_ID,
      features: featureParam,
      source: sourceParam,
      pageSize,
    },
    {
      skip: isSingleGroupView,
    }
  );

  const {
    data: customerAuditLog,
    isLoading: isCustomerLoading,
    isFetching: isCustomerFetching,
    isSuccess: isCustomerSuccess,
  } = useGetCompanyGroupAuditLogsQuery(
    {
      pageNum: Number(page),
      groupId: groupId,
      startDate: filterValues.START_DATE,
      endDate: filterValues.END_DATE,
      entityId: filterValues.ENDITY_ID,
      features: featureParam,
      source: sourceParam,
      pageSize,
    },
    {
      skip: !isSingleGroupView || blocked || permission?.isLoading,
    }
  );

  const auditlogs = isSingleGroupView ? customerAuditLog : data;
  const isLoading = isSingleGroupView ? isCustomerLoading : isAdminLoading;
  const isSuccess = isSingleGroupView ? isCustomerSuccess : isAdminSuccess;

  const tableData = useMemo(
    () => (isSingleGroupView ? customerAuditLog?.logs : data?.logs),
    [customerAuditLog, data, isSingleGroupView]
  );

  const handleRowClick = (log: AuditLog) => {
    if (log.obj?.uuid && log.company_entity?.uuid) {
      history.push(`${url}/${log.uuid}${search}`);
    }
  };

  const handleSliderClose = () => {
    history.push(`${url}${search}`);
  };

  const table = useReactTable({
    data: tableData || defaultData,
    columns: auditLogColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
    },
  });

  return (
    <DashboardContainer className="t-gap-4 t-h-full">
      <DashboardContainer.Header>
        <div className="t-flex t-justify-between sm:t-flex-col sm:t-items-start sm:t-gap-3">
          <AuditLogFilter
            filterValues={filterValues}
            updateFilter={updateFilter}
            isSingleGroupView={isSingleGroupView}
          />
          <div className="t-flex t-gap-2">
            <PaginationHandler
              page_num={Number(page)}
              per_page={auditlogs?.per_page || 1}
              total_count={auditlogs?.total_count || 0}
              total_pages={auditlogs?.total_pages || 1}
              showOptions
              isLoading={isAdminFetching || isCustomerFetching}
            />
          </div>
        </div>
      </DashboardContainer.Header>
      <DashboardContainer.Content>
        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isEmpty={table.getRowModel().rows.length === 0}
        >
          <Async.Success>
            <div className="t-pb-20">
              <TableUI
                size="regular"
                table={table}
                onRowClick={(row) => handleRowClick(row.original)}
                isHeaderCellUpperCase={false}
              />
            </div>
          </Async.Success>
          <Async.Empty>
            <div className="t-flex t-flex-col t-justify-center t-items-center t-py-16 t-w-full">
              <Wind className="t-text-neutral-40" />
              <div className="t-text-subtext t-text-text-60 t-mt-4">
                No audit logs found
              </div>
              <div className="t-text-body-sm t-text-text-30 t-mb-6 t-text-center t-max-w-96">
                Try adjusting or clearing your filters to see more results
              </div>
              {filterValues !== initialFilterValue && (
                <Button size="xsmall" onClick={resetFilter}>
                  Clear all filters
                </Button>
              )}
            </div>
          </Async.Empty>
        </Async.Root>
      </DashboardContainer.Content>

      <Route path={`${url}/:logId`}>
        {({ match: logMatch }) => {
          const log =
            tableData?.find((l) => l.uuid === logMatch?.params.logId) || null;
          return <AuditLogSlider log={log} onClose={handleSliderClose} />;
        }}
      </Route>
    </DashboardContainer>
  );
};

export const AuditLogs = () => {
  usePageTitle("Audit log");
  const entityId = useCurrentEntityId();
  const { groupId: groupIdFromParams } = useParams<{ groupId: string }>();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isCustomer } = useRoleBasedView();

  const isSingleGroupView = isCustomer || Boolean(groupIdFromParams);

  return (
    <DashboardLayout header={<Header v2 title="Audit log" />}>
      <AuditLogsTable
        entityId={entityId}
        groupId={groupId}
        isSingleGroupView={isSingleGroupView}
      />
    </DashboardLayout>
  );
};
